import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsService } from "../forms.service";
import { ReferralFormQuestionGroup } from "../../shared/models/referral-form-question-group.model";
import { MatDrawer } from '@angular/material/sidenav';
import { CdkDragDrop,CdkDrag,CdkDropList,CdkDropListGroup, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import * as _ from 'lodash';
import { finalize } from 'rxjs/operators';
import { ReferralForm } from "../../shared/models/referral-form.model";
import { FeedbackService } from "../../shared/services/feedback.service";
import { HttpResponseCodes } from "../../shared/enums/http-respose-codes.enum";
import { QuestionGroupsService } from "../../question-groups/question-groups.service";
import { IQuestionGroup } from '../../shared/models/interfaces/question.group';
import { IReferralFormType } from "../../shared/models/interfaces/referral-form-type";
import { NgForm } from '@angular/forms';
import { IReferralForm } from '../../shared/models/interfaces/referral-form';
import { takeUntil, catchError, tap } from 'rxjs/operators';
import { Subject, of, forkJoin } from 'rxjs';

@Component({
  selector: 'app-save-form',
  templateUrl: './save-form.component.html',
  styleUrls: ['./save-form.component.scss'],
  providers: [
    FormsService,
    QuestionGroupsService,
    FeedbackService
  ]
})
export class SaveFormComponent implements OnInit, OnDestroy {
  @ViewChild('Drawer', { static: false }) drawer: MatDrawer;
  @ViewChild('editReferralForm', { static: true }) form: NgForm;
  private destroy$ = new Subject<void>();
  isEditMode = false;
  isLoading: boolean = false;
  referralFormId: number;
  referralFormTypes: IReferralFormType[];
  referralFormQuestionGroups: IQuestionGroup[];
  questionGroups: IQuestionGroup[];
  referralForm: ReferralForm | null;
  allActiveNonDeletedReferralFormList: IReferralForm[];
  private toggle: boolean = false;
  private readonly formTypeConflictErrorMessage: string = 'Another form already exists for this type';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formsService: FormsService,
    private readonly questionGroupService: QuestionGroupsService,
    private readonly feedbackService: FeedbackService) {
    this.referralFormTypes = [];
    this.referralFormQuestionGroups = [];
    this.questionGroups = [];
  }

  ngOnInit() {
    this.isLoading = true;
    this.fetchAllReferralFormTypes();
    this.fetchReferralFormsWithoutChildren();

    this.route.url.subscribe((url) => {
      if (url && url[1]) {
        var task = url[1].path.toLowerCase();
        if (task === 'create') {
          this.fetchAllQuestionGroups();
        }
        else if (task === 'edit') {

          this.route.params.pipe(
            takeUntil(this.destroy$),
            tap(params => {
              if (params && params["id"]) {
                this.referralFormId = +params["id"];
                const rfObservable = this.loadReferralForm(this.referralFormId);
                const rfqgObservable = this.formsService.getAllReferralFormQuestionGroups(this.referralFormId);
                const qgObservable = this.questionGroupService.getAllQuestionGroups();

                this.isLoading = true;
                forkJoin([rfObservable, rfqgObservable, qgObservable]).pipe(
                  tap((responses) => {
                    if (responses) {
                      this.referralForm = responses[0];
                      if (this.referralForm) {
                        //referral form question groups
                        var rfqg = responses[1];
                        if (rfqg && rfqg.length > 0) {
                          _.each(rfqg,
                            a => {
                              this.referralFormQuestionGroups.push(a.questionGroup);
                            });
                        }
                        //question groups
                        var qg = responses[2];
                        if (qg && qg.length > 0) {
                          _.each(qg,
                            data => {
                              if (!this.referralFormQuestionGroups.find(a => a.questionGroupId === data.questionGroupId)) {
                                this.questionGroups.push(data);
                              }
                            });
                        }
                      } else {
                        this.feedbackService.error('Unable to load / find referral form ');
                      }
                    } else {
                      this.feedbackService.error('An error occurred loading data');
                    }
                  }),
                  catchError(() => {
                    this.feedbackService.error('An error occurred in loading the page');
                    return [];
                  }),
                  tap(() => {
                    this.isLoading = false;
                  })
                ).subscribe();
              }
            })
          ).subscribe();
        }
      }
    });

  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  drop(event: CdkDragDrop<string>) {
    if (event.previousContainer === event.container) {
      if (event.container.data.toString() === 'referralFormQuestionGroups') {
        moveItemInArray(this.referralFormQuestionGroups, event.previousIndex, event.currentIndex);
      }
    } else {
      if (event.previousContainer.data.toString() === 'referralFormQuestionGroups') {
        transferArrayItem(
          this.referralFormQuestionGroups,
          this.questionGroups,
          event.previousIndex,
          event.currentIndex);
      } else {
        transferArrayItem(
          this.questionGroups,
          this.referralFormQuestionGroups,
          event.previousIndex,
          event.currentIndex);
      }
    }
  }

  private fetchAllQuestionGroups() {
    this.isLoading = true;
    this.questionGroupService.getAllQuestionGroups().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (!response) {
          this.feedbackService.show("Issue with loading Question Groups");
        }
        this.questionGroups = response;
        this.showFormDetails();
      },
      error: (error) => {
        this.feedbackService.error(error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  private loadReferralForm(referralFormId: number) {
    this.isLoading = true;
    if (referralFormId) {
      return this.formsService.getReferralFormById(referralFormId).pipe(
        tap((response) => {
          if (!response) {
            this.feedbackService.show('This submitted form does not exist');
          }
          this.referralForm = response;
        }),
        catchError((error) => {
          this.feedbackService.error(error);
          this.isLoading = false;
          return of(null);
        })
      );
    } else {
      this.feedbackService.error('Invalid form');
      this.isLoading = false;
      return of(null);
    }
  }


  private fetchAllReferralFormTypes() {
    this.isLoading = true;
    this.formsService.getAllReferralFormTypes().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (!response) {
          this.feedbackService.show("Issue with loading form types");
        }
        this.referralFormTypes = response;
      },
      error: (error) => {
        this.feedbackService.error(error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  private fetchReferralFormsWithoutChildren() {
    this.isLoading = true;
    this.formsService.getAllReferralFormsWithoutChildren().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (!response) {
          this.feedbackService.show("Issue with loading Referral Forms Without Children");
        }
        this.allActiveNonDeletedReferralFormList = response.filter(x => x.active && !x.isDeleted);
      },
      error: (error) => {
        this.feedbackService.error(error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  save() {
    if (this.referralForm) {
      this.referralForm.referralFormQuestionGroups = [];
      _.each(this.referralFormQuestionGroups,
        (questionGroup) => {
          var rfqg = new ReferralFormQuestionGroup();
          rfqg.referralFormId = this.referralFormId;
          rfqg.questionGroupId = questionGroup.questionGroupId;
          rfqg.index = this.referralForm!.referralFormQuestionGroups.length + 1;
          this.referralForm!.referralFormQuestionGroups.push(rfqg);
        });

      // check if multiple forms of the same type exist then return with error
      if (this.referralForm.active && this.allActiveNonDeletedReferralFormList &&
        this.allActiveNonDeletedReferralFormList.filter(x => x.referralFormId !== this.referralForm!.referralFormId
          && x.referralFormTypeId == this.referralForm!.referralFormTypeId).length >= 1) {
        this.feedbackService.error("Cannot have multiple active referral forms of the same type. Disable existing form of this type and then try to save again.");
        return;
      }

      this.isLoading = true;

      if (this.isEditMode) {
        // this.referralForm.isDeleted = !this.referralForm.isDeleted;
        this.formsService.updateReferralForm(this.referralForm).subscribe(
          (response) => {
            this.referralForm = response;
            this.fetchReferralFormsWithoutChildren();
            this.feedbackService.success('Saved');
            this.isLoading = false;
            this.hideFormDetails();
          },
          (error) => {
            this.isLoading = false;
            if (error.status === HttpResponseCodes.conflict) {
              this.feedbackService.error(this.formTypeConflictErrorMessage);
            } else {
              this.feedbackService.error('An error occurred while saving the form');
            }
          });
      } else {
        const newForm = new ReferralForm();
        newForm.name = this.referralForm.name;
        newForm.description = this.referralForm.description;
        newForm.referralFormTypeId = this.referralForm.referralFormTypeId;
        newForm.referralFormQuestionGroups = this.referralForm.referralFormQuestionGroups;
        newForm.version = 1;
        newForm.active = false;
        this.formsService.createReferralForm(newForm).subscribe(
          () => {
            this.fetchReferralFormsWithoutChildren();
            this.feedbackService.success('Form saved');
            this.isLoading = false;
            this.router.navigate(['forms']);
          },
          (error) => {
            this.isLoading = false;
            if (error.status === HttpResponseCodes.conflict) {
              this.feedbackService.error(this.formTypeConflictErrorMessage);
            } else {
              this.feedbackService.error('An error occured while creating the form');
            }
          });
      }
    }
  }

  showFormDetails() {
    this.drawer.open();
  }

  hideFormDetails() {
    this.drawer.close();
  }

  isSaveDisabled(): boolean {
    return !this.form.valid;
  }

  clickEvent($event: boolean) {
    //if you just want to toggle the class; change toggle variable.
    //this.toggle = !this.toggle;
    this.toggle = $event;
  }
}
